import { Card, IconButton, Typography } from "@mui/material";
import { classes } from "common/theme/theme";
import { DateTime } from "luxon";
import { IconImage } from "portal/components/images/IconImage";
import { LibraryCardDetailRow } from "./LibraryCardDetailRow";
import { MuiIcon } from "common/components/types";
import { useTranslation } from "react-i18next";
import ImplementIcon from "@mui/icons-material/Agriculture";
import React from "react";

export interface EquipmentLibraryCardData<T> {
  /** underlying data entity represented by this library card; not displayed */
  entity: T;

  /** customer-provided name for this piece of equipment; displayed in all variants */
  name?: string;

  /**
   * secondary label for this piece of equipment, providing one specific
   * detail (usually model name); displayed only in small variant
   */
  secondaryLabel?: string;

  /** updated-at timestamp in ISO format; displayed only in medium variant */
  updatedAt?: string;
  /** tabular details; displayed only in medium variant */
  details: { label: string; value: JSX.Element }[];
}

interface EquipmentLibraryCardProps<T> {
  className?: string;
  data: EquipmentLibraryCardData<T>;
  variant?: "medium" | "medium-no-image" | "small" | "small-no-image";
  onCardClick?: React.EventHandler<React.MouseEvent>;
  ActionButtonIcon?: MuiIcon;
  onActionButtonClick?: React.EventHandler<React.MouseEvent>;
}

export const EquipmentLibraryCard = <T,>({
  className,
  data,
  variant = "medium",
  onCardClick,
  ActionButtonIcon,
  onActionButtonClick,
}: EquipmentLibraryCardProps<T>): ReturnType<
  React.FC<EquipmentLibraryCardProps<T>>
> => {
  const { t } = useTranslation();

  switch (variant) {
    case "small":
    case "small-no-image": {
      const drawImage = variant === "small";

      return (
        <Card
          className={classes(className, "flex flex-row gap-5 rounded-xl", {
            "cursor-pointer": onCardClick,
          })}
          onClick={onCardClick}
        >
          {drawImage ? (
            /* placeholder for image of the tractor, which we currently don't store */
            <div className="aspect-[1.8] w-[calc(33%-0.625rem)]">
              <IconImage icon={() => <ImplementIcon fontSize="large" />} />
            </div>
          ) : undefined}
          <div
            className={classes("flex flex-col flex-1 py-3", {
              "px-3": !drawImage,
              "pr-3": drawImage,
            })}
          >
            <div className="flex flex-row">
              <Typography className="flex-1 mb-3 text-lg">
                {data.name}
              </Typography>
              {ActionButtonIcon && (
                <IconButton
                  className="p-0 w-6 h-6"
                  onClick={(e) => {
                    if (onActionButtonClick) {
                      onActionButtonClick(e);
                      e.stopPropagation();
                    }
                  }}
                >
                  <ActionButtonIcon />
                </IconButton>
              )}
            </div>
            <Typography color="secondary">{data.secondaryLabel}</Typography>
          </div>
        </Card>
      );
    }
    case "medium":
    case "medium-no-image": {
      let modifiedAtLabel: string | undefined;
      const drawImage = variant === "medium";

      if (data.updatedAt) {
        const humanReadableDate = DateTime.fromISO(data.updatedAt);
        modifiedAtLabel = t("views.autotractor.equipment.modifiedAt", {
          date: humanReadableDate.toLocaleString(DateTime.DATE_SHORT),
        });
      }

      return (
        <Card
          className={classes(className, "flex flex-row p-5 gap-5 rounded-xl")}
          onClick={onCardClick}
        >
          {drawImage ? (
            /* placeholder for image of the implement, which we currently don't store */
            <div className="aspect-[1.8] w-[calc(50%-0.625rem)]">
              <IconImage icon={() => <ImplementIcon fontSize="large" />} />
            </div>
          ) : undefined}
          <div className="flex flex-col flex-1">
            <div className="flex flex-row">
              <Typography className="flex-1 mb-3 text-lg">
                {data.name}
              </Typography>
              {ActionButtonIcon && (
                <IconButton
                  className="p-0 w-6 h-6"
                  onClick={(e) => {
                    if (onActionButtonClick) {
                      onActionButtonClick(e);
                      e.stopPropagation();
                    }
                  }}
                >
                  <ActionButtonIcon />
                </IconButton>
              )}
            </div>
            {data.details.map((detail, index) => (
              <LibraryCardDetailRow
                key={index}
                label={detail.label}
                value={<>{detail.value}</>}
              />
            ))}
            <div className="flex-1" />
            <Typography color="secondary">{modifiedAtLabel}</Typography>
          </div>
        </Card>
      );
    }
  }
};
